/**
 * File Auto-Save - Automatic saving to local files using File System Access API
 *
 * Only available in Chromium-based browsers (Chrome, Edge, Opera)
 * Disabled for demo sessions
 */

const DB_NAME = 'hex-map-file-handles';
const DB_VERSION = 1;
const STORE_NAME = 'fileHandles';
const HANDLE_KEY = 'auto-save-handle';

/**
 * Check if File System Access API is supported (Chromium browsers)
 * @returns {boolean}
 */
export function isFileSystemAccessSupported() {
  return 'showSaveFilePicker' in window;
}

/**
 * Check if browser is Chromium-based
 * @returns {boolean}
 */
export function isChromiumBrowser() {
  // Check for Chrome, Edge, Opera, or other Chromium browsers
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isEdge = /Edg/.test(navigator.userAgent);
  const isOpera = /OPR/.test(navigator.userAgent);

  return (isChrome || isEdge || isOpera) && isFileSystemAccessSupported();
}

/**
 * Open IndexedDB database for storing file handles
 * @returns {Promise<IDBDatabase>}
 */
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Store file handle in IndexedDB
 * @param {FileSystemFileHandle} fileHandle
 * @returns {Promise<void>}
 */
async function storeFileHandle(fileHandle) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(fileHandle, HANDLE_KEY);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get stored file handle from IndexedDB
 * @returns {Promise<FileSystemFileHandle|null>}
 */
async function getFileHandle() {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(HANDLE_KEY);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.warn('Failed to get file handle:', error);
    return null;
  }
}

/**
 * Remove stored file handle from IndexedDB
 * @returns {Promise<void>}
 */
async function removeFileHandle() {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(HANDLE_KEY);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
    transaction.oncomplete = () => db.close();
  });
}

/**
 * Initialize auto-save by prompting user to select a file
 * @param {string} suggestedName - Default filename
 * @returns {Promise<FileSystemFileHandle|null>}
 */
export async function initializeAutoSave(suggestedName = 'session.json') {
  if (!isFileSystemAccessSupported()) {
    throw new Error('File System Access API not supported');
  }

  try {
    const fileHandle = await window.showSaveFilePicker({
      suggestedName,
      types: [{
        description: 'Hex Map Session',
        accept: { 'application/json': ['.json'] }
      }]
    });

    await storeFileHandle(fileHandle);
    return fileHandle;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('User cancelled file picker');
      return null;
    }
    throw error;
  }
}

/**
 * Save session data to the linked file
 * @param {Object} session - Session object
 * @param {Object} mapData - Map data object
 * @returns {Promise<boolean>} True if saved successfully
 */
export async function autoSaveToFile(session, mapData) {
  const fileHandle = await getFileHandle();
  if (!fileHandle) {
    console.warn('No file linked for auto-save');
    return false;
  }

  try {
    // Request permission to write (may prompt user if permission was revoked)
    const permission = await fileHandle.queryPermission({ mode: 'readwrite' });
    if (permission !== 'granted') {
      const newPermission = await fileHandle.requestPermission({ mode: 'readwrite' });
      if (newPermission !== 'granted') {
        console.warn('Write permission denied');
        return false;
      }
    }

    // Write to file
    const writable = await fileHandle.createWritable();
    const content = JSON.stringify({ session, mapData }, null, 2);
    await writable.write(content);
    await writable.close();

    console.log('[File Auto-Save] Saved to:', fileHandle.name);
    return true;
  } catch (error) {
    console.error('[File Auto-Save] Failed:', error);
    return false;
  }
}

/**
 * Check if a file is currently linked for auto-save
 * @returns {Promise<{linked: boolean, fileName: string|null}>}
 */
export async function getAutoSaveStatus() {
  const fileHandle = await getFileHandle();
  if (!fileHandle) {
    return { linked: false, fileName: null };
  }

  try {
    // Verify we still have permission
    const permission = await fileHandle.queryPermission({ mode: 'readwrite' });
    return {
      linked: permission === 'granted',
      fileName: fileHandle.name
    };
  } catch (error) {
    return { linked: false, fileName: null };
  }
}

/**
 * Unlink the current auto-save file
 * @returns {Promise<void>}
 */
export async function unlinkAutoSaveFile() {
  await removeFileHandle();
  console.log('[File Auto-Save] File unlinked');
}

/**
 * Auto-save manager class to track changes and trigger saves
 */
export class AutoSaveManager {
  constructor(threshold = 30) {
    this.threshold = threshold;
    this.changeCount = 0;
    this.isEnabled = false;
    this.lastSaveTime = 0;
  }

  /**
   * Enable auto-save for this session
   */
  enable() {
    this.isEnabled = true;
    this.changeCount = 0;
  }

  /**
   * Disable auto-save
   */
  disable() {
    this.isEnabled = false;
    this.changeCount = 0;
  }

  /**
   * Record a change and potentially trigger auto-save
   * @param {Object} session
   * @param {Object} mapData
   * @returns {Promise<boolean>} True if auto-save was triggered
   */
  async recordChange(session, mapData) {
    if (!this.isEnabled) {
      return false;
    }

    this.changeCount++;

    if (this.changeCount >= this.threshold) {
      this.changeCount = 0;
      this.lastSaveTime = Date.now();
      const success = await autoSaveToFile(session, mapData);

      if (success) {
        // Dispatch event for UI feedback
        document.dispatchEvent(new CustomEvent('auto-save-complete', {
          detail: { timestamp: this.lastSaveTime }
        }));
      }

      return success;
    }

    return false;
  }

  /**
   * Force an immediate auto-save
   * @param {Object} session
   * @param {Object} mapData
   * @returns {Promise<boolean>}
   */
  async forceSave(session, mapData) {
    if (!this.isEnabled) {
      return false;
    }

    this.changeCount = 0;
    this.lastSaveTime = Date.now();
    const success = await autoSaveToFile(session, mapData);

    if (success) {
      document.dispatchEvent(new CustomEvent('auto-save-complete', {
        detail: { timestamp: this.lastSaveTime }
      }));
    }

    return success;
  }

  /**
   * Get current status
   * @returns {Object}
   */
  getStatus() {
    return {
      enabled: this.isEnabled,
      changeCount: this.changeCount,
      threshold: this.threshold,
      lastSaveTime: this.lastSaveTime
    };
  }
}
